import { collectClientHealth } from './health';
import { currentOfflineAuthorizationEnvelope } from './authEnvelope';
import { getAllByIndex, getDatabaseHealth, getLocalSchemaVersion } from './db';
import { getDeviceId } from './device';
import type { ConflictRecord, OutboxCommand } from './types';

export interface OfflineDiagnosticPackage {
  generated_at: string;
  device_id: string;
  client_health: Awaited<ReturnType<typeof collectClientHealth>>;
  local_database: { schema_version: string; health: string };
  authorization: { present: boolean; valid_until: string | null; policy_version: string | null };
  command_counts: Record<string, number>;
  conflict_counts: Record<string, number>;
  privacy_note: string;
}

export async function buildOfflineDiagnosticPackage(userId?: string | null): Promise<OfflineDiagnosticPackage> {
  const health = await collectClientHealth(userId || null);
  const envelope = userId ? await currentOfflineAuthorizationEnvelope(userId) : null;
  const commandCounts: Record<string, number> = {};
  for (const status of ['PENDING','READY','SYNCING','ACKNOWLEDGED','FAILED_RETRYABLE','FAILED_PERMANENT','CONFLICT','CANCELLED'] as const) {
    const rows = await getAllByIndex<OutboxCommand>('outbox_commands', 'byStatusPriority', IDBKeyRange.bound([status,'P0',0],[status,'P4',Number.MAX_SAFE_INTEGER]));
    commandCounts[status] = rows.filter(row => !userId || row.user_id === userId).length;
  }
  const conflictCounts: Record<string, number> = {};
  for (const status of ['OPEN','AUTO_RESOLVED','USER_ACTION_REQUIRED','ADMIN_REVIEW_REQUIRED','RESOLVED'] as const) {
    const rows = await getAllByIndex<ConflictRecord>('conflicts', 'byStatus', IDBKeyRange.only(status));
    conflictCounts[status] = rows.filter(row => !userId || row.user_id === userId).length;
  }
  return {
    generated_at: new Date().toISOString(),
    device_id: getDeviceId(),
    client_health: health,
    local_database: { schema_version: await getLocalSchemaVersion(), health: getDatabaseHealth().state },
    authorization: { present: !!envelope, valid_until: envelope?.offline_valid_until || null, policy_version: envelope?.policy_version || null },
    command_counts: commandCounts,
    conflict_counts: conflictCounts,
    privacy_note: 'This diagnostic package intentionally excludes farmer identities, GPS coordinates, command payloads, tokens, cookies, and local encryption keys.',
  };
}

export async function downloadOfflineDiagnosticPackage(userId?: string | null): Promise<void> {
  const data = await buildOfflineDiagnosticPackage(userId);
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = `coconnect-offline-diagnostics-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
